import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../app/store';
import type { LEDContentType, LEDBackgroundType } from '../screens/LEDScreen';
import type { BroadcastContentType, LowerThirdPosition, BroadcastBackgroundType } from '../screens/BroadcastScreen';

/**
 * Target for visual control commands
 */
export type VisualTarget = 'led' | 'broadcast' | 'both';

/**
 * Visual Control Command - sent from controller to LED/Broadcast
 */
export interface VisualControlCommand {
  target: VisualTarget;
  contentType: LEDContentType | BroadcastContentType;
  visible: boolean;
  // Content data (participant, result, etc.)
  data?: VisualContentData;
  // Background settings (LED)
  background?: {
    type: LEDBackgroundType;
    color?: string;
    imageUrl?: string;
    videoUrl?: string;
  };
  // Background settings (Broadcast)
  broadcastBackground?: {
    type: BroadcastBackgroundType;
    color?: string;
    comboMode?: boolean;
  };
  // Lower third position (Broadcast only)
  lowerThirdPosition?: LowerThirdPosition;
  // Broadcast theme ID
  themeId?: string;
  // Auto-heats mode toggle
  autoHeatsEnabled?: boolean;
}

/**
 * Content data that can be displayed
 */
export interface VisualContentData {
  // Single participant/result
  participant?: {
    startNumber: number;
    name: string;
    country: string;
    flagUrl?: string;
    customFlag?: string;
  };
  // Couple (for partner dances)
  couple?: {
    startNumber: number;
    leader: string;
    follower: string;
    country: string;
    club?: string;
    flagUrl?: string;
    customFlag?: string;
  };
  // Results data
  result?: {
    place: number;
    scores?: Record<string, number | string>;
    dances?: string[];
  };
  // Start list / Heat data
  participants?: Array<{
    startNumber: number;
    name?: string;
    country?: string;
    flagUrl?: string;
    customFlag?: string;
  }>;
  // Round info
  round?: {
    name: string;
    categoryName: string;
    dances?: string[];
  };
  // Competition info
  competition?: {
    city: string;
    country: string;
  };
  // Adjudicator(s)
  adjudicator?: {
    letter: string;
    name: string;
    country?: string;
    role?: string; // 'Judge', 'Chairperson', 'Scrutineer'
  };
  adjudicators?: Array<{
    letter: string;
    name: string;
    country?: string;
    role?: string;
  }>;
  // Podium data
  podium?: {
    gold: { name: string; country: string; flagUrl?: string; customFlag?: string };
    silver: { name: string; country: string; flagUrl?: string; customFlag?: string };
    bronze: { name: string; country: string; flagUrl?: string; customFlag?: string };
  };
  // Heat numbers
  heats?: {
    current: number;
    next?: number;
    currentParticipants: number[];
    nextParticipants?: number[];
    nextDanceName?: string;
  };
  // Current dance info
  currentDance?: {
    danceName: string;
    danceNumber?: number;
    totalDances?: number;
  };
  // Solo participant
  soloParticipant?: {
    startNumber: number;
    name: string;
    country: string;
    club?: string;
    flagUrl?: string;
    customFlag?: string;
  };
  // Officials list
  officials?: Array<{
    role: string;
    name: string;
    country?: string;
    letter?: string;
  }>;
  // Results list (for dance-results / total-results screens)
  results?: Array<{
    place: number;
    startNumber?: number;
    name: string;
    country?: string;
    scores?: Record<string, number | string>;
    score?: string;
    customFlag?: string;
  }>;
  // Country flag (full screen display)
  countryFlag?: {
    country: string;
    flagUrl?: string;
    customFlag?: string;
  };
  // Flag wave animation
  waveEffect?: boolean;
}

/**
 * Current visual state - synced across all clients
 */
export interface VisualState {
  // Auto-heats mode: MC/Chairperson devices auto-send next heat on completion
  autoHeatsEnabled: boolean;
  // LED Screen state
  led: {
    visible: boolean;
    contentType: LEDContentType;
    data?: VisualContentData;
    background: {
      type: LEDBackgroundType;
      color: string;
      imageUrl?: string;
      videoUrl?: string;
    };
  };
  // Broadcast state
  broadcast: {
    visible: boolean;
    contentType: BroadcastContentType;
    data?: VisualContentData;
    lowerThirdPosition: LowerThirdPosition;
    themeId: string;
    background: {
      type: BroadcastBackgroundType;
      color: string;
      comboMode?: boolean;
    };
  };
}

const initialState: VisualState = {
  autoHeatsEnabled: false,
  led: {
    visible: false,
    contentType: 'idle',
    background: {
      type: 'solid-color',
      color: '#000000',
    },
  },
  broadcast: {
    visible: false,
    contentType: 'idle',
    lowerThirdPosition: 'bottom-left',
    themeId: 'wdsf-gold',
    background: {
      type: 'theme-background',
      color: '#000000',
      comboMode: false,
    },
  },
};

const visualSlice = createSlice({
  name: 'visual',
  initialState,
  reducers: {
    /**
     * Update the full visual state (received from server)
     */
    setVisualState(state, action: PayloadAction<VisualState>) {
      return action.payload;
    },

    /**
     * Apply a visual control command (updates local state)
     */
    applyVisualControl(state, action: PayloadAction<VisualControlCommand>) {
      const { target, contentType, visible, data, background, broadcastBackground, lowerThirdPosition } = action.payload;

      // Handle autoHeatsEnabled toggle
      if (action.payload.autoHeatsEnabled !== undefined) {
        state.autoHeatsEnabled = action.payload.autoHeatsEnabled;
      }
      // Auto-disable when content changes to non-heats
      if (contentType && contentType !== 'heats') {
        state.autoHeatsEnabled = false;
      }

      if (target === 'led' || target === 'both') {
        state.led.visible = visible;
        state.led.contentType = contentType as LEDContentType;
        if (data !== undefined) {
          state.led.data = data;
        }
        if (background) {
          state.led.background = {
            ...state.led.background,
            ...background,
          };
        }
      }

      if (target === 'broadcast' || target === 'both') {
        state.broadcast.visible = visible;
        state.broadcast.contentType = contentType as BroadcastContentType;
        if (data !== undefined) {
          state.broadcast.data = data;
        }
        if (lowerThirdPosition) {
          state.broadcast.lowerThirdPosition = lowerThirdPosition;
        }
        if (action.payload.themeId) {
          state.broadcast.themeId = action.payload.themeId;
        }
        if (broadcastBackground) {
          state.broadcast.background = {
            ...state.broadcast.background,
            ...broadcastBackground,
          };
        }
      }
    },

    /**
     * Set LED visibility
     */
    setLedVisible(state, action: PayloadAction<boolean>) {
      state.led.visible = action.payload;
    },

    /**
     * Set Broadcast visibility
     */
    setBroadcastVisible(state, action: PayloadAction<boolean>) {
      state.broadcast.visible = action.payload;
    },

    /**
     * Set LED content type
     */
    setLedContentType(state, action: PayloadAction<LEDContentType>) {
      state.led.contentType = action.payload;
    },

    /**
     * Set Broadcast content type
     */
    setBroadcastContentType(state, action: PayloadAction<BroadcastContentType>) {
      state.broadcast.contentType = action.payload;
    },

    /**
     * Set LED content data
     */
    setLedData(state, action: PayloadAction<VisualContentData | undefined>) {
      state.led.data = action.payload;
    },

    /**
     * Set Broadcast content data
     */
    setBroadcastData(state, action: PayloadAction<VisualContentData | undefined>) {
      state.broadcast.data = action.payload;
    },

    /**
     * Set LED background
     */
    setLedBackground(state, action: PayloadAction<VisualState['led']['background']>) {
      state.led.background = action.payload;
    },

    /**
     * Set Broadcast background
     */
    setBroadcastBackground(state, action: PayloadAction<VisualState['broadcast']['background']>) {
      state.broadcast.background = action.payload;
    },

    /**
     * Set broadcast theme
     */
    setBroadcastTheme(state, action: PayloadAction<string>) {
      state.broadcast.themeId = action.payload;
    },

    /**
     * Clear all visual outputs (go to idle)
     */
    clearAllVisuals(state) {
      state.autoHeatsEnabled = false;
      state.led.visible = false;
      state.led.contentType = 'idle';
      state.led.data = undefined;
      state.broadcast.visible = false;
      state.broadcast.contentType = 'idle';
      state.broadcast.data = undefined;
    },

    /**
     * Set auto-heats mode
     */
    setAutoHeats(state, action: PayloadAction<boolean>) {
      state.autoHeatsEnabled = action.payload;
    },
  },
});

// Export actions
export const {
  setVisualState,
  applyVisualControl,
  setLedVisible,
  setBroadcastVisible,
  setLedContentType,
  setBroadcastContentType,
  setLedData,
  setBroadcastData,
  setLedBackground,
  setBroadcastBackground,
  setBroadcastTheme,
  clearAllVisuals,
  setAutoHeats,
} = visualSlice.actions;

// Selectors
export const selectVisualState = (state: RootState) => state.visual;
export const selectLedState = (state: RootState) => state.visual.led;
export const selectBroadcastState = (state: RootState) => state.visual.broadcast;
export const selectLedVisible = (state: RootState) => state.visual.led.visible;
export const selectBroadcastVisible = (state: RootState) => state.visual.broadcast.visible;
export const selectLedContentType = (state: RootState) => state.visual.led.contentType;
export const selectBroadcastContentType = (state: RootState) => state.visual.broadcast.contentType;
export const selectLedData = (state: RootState) => state.visual.led.data;
export const selectBroadcastData = (state: RootState) => state.visual.broadcast.data;
export const selectLedBackground = (state: RootState) => state.visual.led.background;
export const selectBroadcastBackground = (state: RootState) => state.visual.broadcast.background;
export const selectBroadcastThemeId = (state: RootState) => state.visual.broadcast.themeId;
export const selectAutoHeatsEnabled = (state: RootState) => state.visual.autoHeatsEnabled;

export default visualSlice.reducer;
